import { createFileRoute, Outlet, useNavigate, useLocation } from '@tanstack/react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import { useCallback, useMemo, useState } from 'react';
import { ConfirmDialog, GenericDetailPage, ProjectInfoBox, Spinner, GeneralNotFound, Tabs } from '@repo/components';
import { PERMISSIONS } from '@repo/shared/domain';
import { ProjectForm } from '@repo/forms';
import { EventEmitter, getVerifyPermission, guardedClient as request } from '@repo/shared/helpers';
import { useBreadcrumbLevel } from '@repo/components/hooks';
import { useLocalStorage } from '@repo/components/hooks';
import { useAuthenticatedUser } from 'src/hooks/useAuthenticatedUser';
import { getProject, archiveProject, restoreProject } from 'src/api/personal/projects';
import { getPrincipalGroupLabelsSettings } from 'src/api/settings';

export const Route = createFileRoute('/personal_/projects/$projectId')({
	component: ProjectLayout,
});

function ProjectLayout() {
	const { projectId } = Route.useParams();
	const navigate = useNavigate();
	const location = useLocation();
	const queryClient = useQueryClient();

	const { user: authenticatedUser, loading: authLoading } = useAuthenticatedUser();
	const verifyPermission = getVerifyPermission(authenticatedUser?.permissions || []);
	const { value: locallyStoredBackUrl } = useLocalStorage<string>('personalDashboardProjectsBackUrl');

	const [isFormOpened, setIsFormOpened] = useState(false);
	const [isProjectArchiveDialogOpen, setIsProjectArchiveDialogOpen] = useState(false);

	const { data: project, isLoading: projectLoading } = useQuery({
		queryKey: ['personal-project', projectId],
		queryFn: () => getProject(projectId),
	});

	const { data: principalGroupLabelsSettings, isLoading: principalGroupLabelsLoading } = useQuery({
		queryKey: ['principal-group-labels-settings'],
		queryFn: () => getPrincipalGroupLabelsSettings(),
	});

	const principalGroupLabels =
		principalGroupLabelsSettings?.settings?.principalGroupLabels?.map((item: { value: string }) => item.value) || [];

	useBreadcrumbLevel(0, 'Personal Dashboard', '/personal');
	useBreadcrumbLevel(1, 'Projects', '/personal/projects');
	useBreadcrumbLevel(2, project?.name || '');

	const pathSegments = location.pathname.split('/');
	const selectedLayoutSegment = (pathSegments[pathSegments.length - 1] || 'events') as 'events' | 'logs';

	const { tabs, tabIndexPerTabId } = useMemo(() => {
		const builtTabs = [
			{ id: 'events', label: 'Events' },
			...(verifyPermission([PERMISSIONS.personalProjectsView]) ? [{ id: 'logs', label: 'Logs' }] : []),
		];

		const tabIndexPerTabId = builtTabs.reduce(
			(acc, cur, index) => ({ ...acc, [cur.id]: index }),
			{} as Record<string, number>,
		);

		return { tabs: builtTabs, tabIndexPerTabId };
	}, [verifyPermission]);

	const handleTabChange = (newValue: number) => {
		const tabId = tabs[newValue].id;
		navigate({ to: `/personal/projects/$projectId/${tabId}`, params: { projectId } });
	};

	const goBack = () => {
		if (locallyStoredBackUrl) {
			try {
				const url = new URL(locallyStoredBackUrl);
				navigate({ to: url.pathname + url.search });
				return;
			} catch {
				// Invalid URL, fallback to default
			}
		}
		navigate({ to: '/personal/projects' });
	};

	const revalidateProject = () => {
		queryClient.invalidateQueries({ queryKey: ['personal-project', projectId] });
	};

	const revalidateProjects = () => {
		queryClient.invalidateQueries({ queryKey: ['personal-projects'] });
	};

	const handleEditProject = async (formData: Record<string, unknown>) => {
		let isError = false;
		if (formData) {
			delete formData.projectData;
			await request
				.put(`/api/personal-dashboard/projects/${projectId}`, formData)
				.then(() => {
					EventEmitter.emit('toast', {
						type: 'success',
						message: 'Project saved',
					});
				})
				.catch((error: { message?: string; body?: { message?: string } }) => {
					isError = true;
					const errorMessage =
						error?.message || error?.body?.message || 'Failed to save Project. Did you enter all data correctly?';
					EventEmitter.emit('toast', {
						type: 'error',
						message: errorMessage,
					});
				});
		}

		revalidateProject();
		revalidateProjects();
		if (!isError) {
			setIsFormOpened(false);
		}
	};

	const handleOpenForm = useCallback(() => {
		setIsFormOpened(true);
	}, []);

	const handleCloseForm = () => {
		setIsFormOpened(false);
	};

	const handleOpenProjectArchiveDialog = () => {
		setIsProjectArchiveDialogOpen(true);
	};

	const handleCloseProjectArchiveDialog = () => {
		setIsProjectArchiveDialogOpen(false);
	};

	const handleRestoreProject = async () => {
		try {
			await restoreProject(projectId);
			EventEmitter.emit('toast', {
				type: 'success',
				message: 'Project restored',
			});
			revalidateProject();
			revalidateProjects();
		} catch {
			EventEmitter.emit('toast', {
				type: 'error',
				message: 'Failed to restore project',
			});
		}
	};

	const handleArchiveProject = async () => {
		try {
			if (!project) throw new Error('No project selected');

			await archiveProject(projectId);
			EventEmitter.emit('toast', {
				type: 'success',
				message: 'Project archived',
			});
			revalidateProject();
			revalidateProjects();
			setIsProjectArchiveDialogOpen(false);
			goBack();
		} catch {
			EventEmitter.emit('toast', {
				type: 'error',
				message: 'Failed to archive project',
			});
		}
	};

	const isLoading = authLoading || projectLoading || principalGroupLabelsLoading;

	if (isLoading) {
		return (
			<div className="h-full w-full flex items-center justify-center p-8">
				<Spinner />
			</div>
		);
	}

	if (!authenticatedUser) {
		return (
			<GeneralNotFound
				label="Authentication required"
				action={{
					label: 'Return to projects',
					href: '/personal/projects',
					variant: 'outlined',
				}}
			/>
		);
	}

	if (!project) {
		return (
			<GeneralNotFound
				label="We couldn't find this project. Maybe it has been deleted?"
				action={{
					label: 'Return to projects',
					href: '/personal/projects',
					variant: 'outlined',
				}}
			/>
		);
	}

	// Determine if we should use simplified form (same logic as in ProjectForm)
	const shouldShowSimplifiedForm = (() => {
		if (principalGroupLabels.length > 1) return false;
		if (project.principalGroups && project.principalGroups.length > 0) {
			const existingLabels = [...new Set(project.principalGroups.map((pg: { label: string }) => pg.label))];
			if (existingLabels.length > 1) return false;
			if (existingLabels.length === 1 && existingLabels[0] !== principalGroupLabels[0]) return false;
		}
		return true;
	})();

	const defaultProjectData = {
		...project,
		...(shouldShowSimplifiedForm && project.principalGroups
			? { groupIds: project.principalGroups.map((pg: { groupId: string }) => pg.groupId) }
			: {}),
	};

	return (
		<>
			<div className="flex justify-center">
				<div className="max-w-[1280px] w-full">
					<GenericDetailPage>
						<GenericDetailPage.Content className="!px-0">
							<GenericDetailPage.Content.Notifications>
								{project.archived && (
									<Alert
										severity="info"
										variant="filled"
										action={
											verifyPermission([PERMISSIONS.personalProjectsArchive]) && (
												<Button color="inherit" onClick={handleRestoreProject}>
													Restore
												</Button>
											)
										}
									>
										This project is archived.
									</Alert>
								)}
							</GenericDetailPage.Content.Notifications>
							<ProjectInfoBox
								project={project}
								permissions={{
									canEdit: verifyPermission([PERMISSIONS.personalProjectsEdit]),
									canArchive: verifyPermission([PERMISSIONS.personalProjectsArchive]),
									canOpenGroupDetail: verifyPermission([PERMISSIONS.personalGroupsView]),
								}}
								openEditForm={handleOpenForm}
								openArchiveDialog={handleOpenProjectArchiveDialog}
							/>
						</GenericDetailPage.Content>
						<Tabs value={tabIndexPerTabId[selectedLayoutSegment] ?? 0} onChange={handleTabChange} className="px-4">
							<Tabs.TabList>
								{tabs.map((tab, index) => (
									<Tabs.Tab key={tab.id} value={index} label={tab.label} />
								))}
							</Tabs.TabList>
						</Tabs>
						<Outlet />
					</GenericDetailPage>
				</div>
			</div>
			<ConfirmDialog
				isOpen={isProjectArchiveDialogOpen}
				cancelAction={handleCloseProjectArchiveDialog}
				confirmAction={handleArchiveProject}
				cancelLabel="Cancel"
				confirmLabel="Archive"
			>
				Are you sure you want to archive this project?
			</ConfirmDialog>

			{isFormOpened && (
				<ProjectForm
					isOpen={isFormOpened}
					onClose={handleCloseForm}
					onSubmit={handleEditProject}
					data={defaultProjectData}
					principalGroupLabels={principalGroupLabels}
				/>
			)}
		</>
	);
}
